import { useState, useEffect, useRef } from 'react';
import {
  Container, Row, Col, Card, CardBody, Button, Badge, Table,
} from 'reactstrap';
import { RefreshCw, Users, Wifi, Send, TrendingUp } from 'react-feather';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';

export default function AdminDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  async function fetchData() {
    try {
      const { data: d } = await api.get('/admin/dashboard');
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    fetchData();
    timer.current = setInterval(fetchData, 60000);
    return () => clearInterval(timer.current);
  }, []);

  if (loading) return (
    <>
      <Breadcrumbs mainTitle='Dashboard Global' parent='Admin' title='Dashboard' />
      <Container fluid>
        <div className='d-flex align-items-center justify-content-center' style={{ height: 200 }}>
          <RefreshCw size={20} className='me-2' style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
        </div>
      </Container>
    </>
  );

  const d = data || {};
  const tenants = d.tenants || [];
  const tenantsChart = tenants.map(t => ({
    name: t.name.length > 18 ? t.name.slice(0, 18) + '…' : t.name,
    Disparos: t.total_sent || 0,
    Campanhas: t.total_campaigns || 0,
  }));

  return (
    <>
      <Breadcrumbs mainTitle='Dashboard Global' parent='Admin' title='Dashboard' />
      <Container fluid>

        {/* Header */}
        <div className='d-flex align-items-center justify-content-between mb-4'>
          <div>
            <h4 className='mb-0 fw-bold'>Dashboard Global</h4>
            <p className='text-muted small mb-0'>Visão consolidada de todos os tenants</p>
          </div>
          <Button color='light' size='sm' onClick={fetchData}>
            <RefreshCw size={13} className='me-1' /> Atualizar
          </Button>
        </div>

        {/* Cards globais */}
        <Row className='mb-4'>
          <Col md={3}>
            <StatCard
              icon={<Users size={18} />}
              label='Tenants ativos'
              value={d.total_tenants ?? 0}
              color='text-purple'
              bg='bg-light'
            />
          </Col>
          <Col md={3}>
            <StatCard
              icon={<Wifi size={18} />}
              label='Conexões totais'
              value={d.total_connections ?? 0}
              color='text-success'
              bg='bg-light'
            />
          </Col>
          <Col md={3}>
            <StatCard
              icon={<Send size={18} />}
              label='Disparos totais'
              value={(d.total_sent ?? 0).toLocaleString('pt-BR')}
              color='text-primary'
              bg='bg-light'
            />
          </Col>
          <Col md={3}>
            <StatCard
              icon={<TrendingUp size={18} />}
              label='Taxa de resposta'
              value={`${d.taxa_resposta ?? 0}%`}
              color='text-warning'
              bg='bg-light'
            />
          </Col>
        </Row>

        {/* Gráfico por tenant */}
        {tenantsChart.length > 0 && (
          <Card className='mb-4'>
            <CardBody>
              <p className='fw-semibold small mb-4'>Disparos por tenant</p>
              <ResponsiveContainer width='100%' height={240}>
                <BarChart data={tenantsChart} layout='vertical' margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                  <XAxis type='number' tick={{ fontSize: 11 }} />
                  <YAxis type='category' dataKey='name' tick={{ fontSize: 11 }} width={140} />
                  <Tooltip />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey='Disparos'  fill='#0ea5e9' radius={[0, 3, 3, 0]} />
                  <Bar dataKey='Campanhas' fill='#a855f7' radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}

        {/* Tabela de tenants */}
        <Card>
          <CardBody className='p-0'>
            <div className='px-4 py-3 border-bottom'>
              <p className='mb-0 fw-semibold small'>Tenants</p>
            </div>
            {tenants.length === 0 ? (
              <div className='p-5 text-center text-muted small'>Nenhum tenant cadastrado</div>
            ) : (
              <div className='table-responsive'>
                <Table hover className='mb-0'>
                  <thead className='table-light'>
                    <tr className='small text-muted text-uppercase'>
                      <th>Tenant</th>
                      <th>E-mail</th>
                      <th className='text-center'>Status</th>
                      <th className='text-end'>Conexões</th>
                      <th className='text-end'>Campanhas</th>
                      <th className='text-end'>Disparos</th>
                      <th className='text-end'>Respostas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map(t => (
                      <tr key={t.id}>
                        <td className='fw-medium small'>{t.name}</td>
                        <td className='small text-muted'>{t.email}</td>
                        <td className='text-center'>
                          <Badge color={
                            t.status === 'active'    ? 'success' :
                            t.status === 'suspended' ? 'danger'  : 'secondary'
                          }>
                            {t.status === 'active' ? 'Ativo' : t.status === 'suspended' ? 'Suspenso' : t.status}
                          </Badge>
                        </td>
                        <td className='text-end small'>{t.total_connections ?? 0}</td>
                        <td className='text-end small'>{t.total_campaigns ?? 0}</td>
                        <td className='text-end small'>{(t.total_sent ?? 0).toLocaleString('pt-BR')}</td>
                        <td className='text-end small'>{t.total_replied ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <p className='text-muted text-end mt-3 mb-0' style={{ fontSize: '0.75rem' }}>
          Atualiza automaticamente a cada 60s
        </p>
      </Container>
    </>
  );
}

function StatCard({ icon, label, value, color, bg }) {
  return (
    <Card>
      <CardBody className='d-flex align-items-center gap-3'>
        <div className={`rounded-2 ${bg} ${color} d-flex align-items-center justify-content-center flex-shrink-0`}
             style={{ width: 40, height: 40 }}>
          {icon}
        </div>
        <div>
          <p className='text-muted text-uppercase mb-0' style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>{label}</p>
          <p className={`h5 fw-bold mb-0 ${color}`}>{value}</p>
        </div>
      </CardBody>
    </Card>
  );
}
